import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { mediaUrl, shuffleStable } from "../common";
import * as s from "./extra.styled";
import { useReport, useSlideState, useReviewMode, addTry } from "../answers";
import { Mock, GrammarVisual } from "./rich";

// data.wordBox: ["cook", "drive", …] — рамка зі словами для вправи (лише показ, без перетягування)
export function WordBox({ words, compact }) {
  if (!words || !words.length) return null;
  return (
    <s.WordBox $compact={compact}>
      {words.map((word) => (
        <span key={word}>{word}</span>
      ))}
    </s.WordBox>
  );
}

// Блоки уроку 2+ (урок 1 їх не використовує). Формат — FORMAT.md, «Доповнення v1.2».

// Порівняння відповіді. strict — з урахуванням регістру й . ? ! (коми ігноруємо): для вправ на великі літери й пунктуацію
const clean = (value = "", strict = false) => {
  const text = value.trim().replace(/[’']/g, "'").replace(/,/g, "").replace(/\s+/g, " ").replace(/\s+([.?!])/g, "$1");
  return strict ? text : text.toLowerCase().replace(/[.?!]+$/, "");
};
const matches = (value, answer, strict) => answer.split("|").some((option) => clean(option, strict) === clean(value, strict));

// **жирний** у тексті правил/табличок
export function Marked({ text }) {
  return String(text)
    .split(/\*\*(.+?)\*\*/g)
    .map((part, i) => (i % 2 ? <s.Mark key={i}>{part}</s.Mark> : <Fragment key={i}>{part}</Fragment>));
}

// Колонки: рядок { heading } починає нову колонку
const toColumns = (lines, columns) => {
  if (!columns || columns < 2) return [lines.map((line, i) => ({ ...line, i }))];
  const groups = [];
  lines.forEach((line, i) => (line.heading || !groups.length ? groups.push([{ ...line, i }]) : groups[groups.length - 1].push({ ...line, i })));
  return groups;
};

// ---------- choose ----------
// Рядок: «I'm from ((*France|French)).» — у ((…)) варіанти через |, правильний позначено *.
// Без ((…)) рядок просто показується (напр. репліка чату). Картинки зверху: data.images [{ url, label, credit }].
const CHOICE = /\(\(([^)]+)\)\)/g;

// poll: true — опитування без правильної відповіді (обране просто підсвічується)
export function ChooseLines({ slideId, lines = [], columns, compact, dense, showAnswers, prefix = "", poll }) {
  const [picked, setPicked] = useSlideState(slideId, "picked", {});
  // неправильні спроби — у зданому ДЗ учитель бачить їх закресленими, а правильну не з першого разу — помаранчевою
  const [tries, setTries] = useSlideState(slideId, "tries", {});
  const review = useReviewMode();

  // Правильні варіанти кожного вибору — для рахунку в ДЗ
  const correctByKey = {};
  lines.forEach((line, i) =>
    String(line.text || "")
      .split(CHOICE)
      .forEach((part, j) => {
        if (j % 2) correctByKey[`${prefix}${i}-${j}`] = (part.split("|").find((o) => o.startsWith("*")) || "").slice(1);
      })
  );
  const keys = Object.keys(correctByKey);
  useReport(slideId, keys.filter((key) => picked[key] === correctByKey[key]).length, keys.length, keys.filter((key) => picked[key]).length);

  const renderLine = (line) => {
    if (line.heading) return <s.ChooseHeading key={`h${line.i}`}>{line.heading}</s.ChooseHeading>;
    const parts = String(line.text || "").split(CHOICE);
    return (
      <s.ChooseLine key={line.i}>
        {line.speaker && <s.Speaker>{line.speaker}:</s.Speaker>}
        <span>
          {parts.map((part, j) => {
            if (j % 2 === 0) return <Marked key={j} text={part} />;
            const key = `${prefix}${line.i}-${j}`;
            const options = part.split("|");
            return (
              <s.Options key={j}>
                {options.map((raw) => {
                  const correct = raw.startsWith("*");
                  const label = correct ? raw.slice(1) : raw;
                  const isPicked = picked[key] === label;
                  const wasTried = review && !isPicked && (tries[key] || []).includes(label);
                  const late = review && correct && (tries[key] || []).length > 0;
                  const state = poll ? null : showAnswers ? (correct ? "ok" : null) : isPicked ? (correct ? (late ? "late" : "ok") : "bad") : wasTried ? "tried" : null;
                  return (
                    <s.Option
                      key={raw}
                      $picked={isPicked}
                      $state={state}
                      onClick={() => {
                        if (!poll && !correct && picked[key] !== label) setTries((prev) => addTry(prev, key, label));
                        setPicked((prev) => ({ ...prev, [key]: label }));
                      }}
                    >
                      {label}
                    </s.Option>
                  );
                })}
              </s.Options>
            );
          })}
        </span>
      </s.ChooseLine>
    );
  };

  const groups = toColumns(lines, columns);
  if (groups.length > 1) {
    return (
      <s.Columns $count={Math.min(groups.length, columns)}>
        {groups.map((group, g) => (
          <s.ChooseLines key={g} $compact={compact} $dense={dense}>
            {group.map(renderLine)}
          </s.ChooseLines>
        ))}
      </s.Columns>
    );
  }
  return (
    <s.ChooseLines $compact={compact} $dense={dense}>
      {groups[0].map(renderLine)}
    </s.ChooseLines>
  );
}

function Choose({ slide, showAnswers }) {
  const { lines = [], images = [], columns, compact, contain, dense } = slide.data || {};
  return (
    <s.ChooseWrap>
      {images.length > 0 && (
        <s.ChooseImages $count={images.length} $compact={compact}>
          {images.map((image) => (
            <s.ChooseImage key={image.url} $contain={contain}>
              <img src={mediaUrl(image.url)} alt="" />
              {image.label && <figcaption>{image.label}</figcaption>}
            </s.ChooseImage>
          ))}
        </s.ChooseImages>
      )}
      <ChooseLines slideId={slide.id} lines={lines} columns={columns} compact={compact || lines.length > 8} dense={dense} showAnswers={showAnswers} poll={slide.data?.poll} />
    </s.ChooseWrap>
  );
}

// ---------- video ----------
// data: { url, lines? (як у choose), start?, end? } — відео з керуванням і питання до нього.
// start/end — частина відео (напр. «перша частина» з книжки); вона вирізана в окремий файл, тож шкала коротка
function Video({ slide, showAnswers }) {
  const { url, lines = [], start, end } = slide.data || {};
  return (
    // Багато питань — вужче відео й компактні рядки, щоб усе влізло поруч
    <s.VideoLayout $wide={lines.length > 4}>
      <s.Video src={mediaUrl(url, { start, end })} controls preload="metadata" playsInline />
      <s.VideoSide>
        <ChooseLines slideId={slide.id} lines={lines} compact={lines.length > 4} showAnswers={showAnswers} />
      </s.VideoSide>
    </s.VideoLayout>
  );
}

// ---------- chat (переписка як у месенджері) ----------
// reading з data.chat: { me: "Lisa" } — абзаци «Ім'я: текст» стають бульбашками; повідомлення «me» — праворуч.
// Повідомлення зʼявляються по одному з «друкує…»; кнопка ↻ — показати ще раз.
function ChatThread({ heading, messages, me, slideId }) {
  const [shown, setShown] = useState(0);
  const [run, setRun] = useState(0);
  const body = useRef(null);
  // чи стоїть стрічка внизу: якщо вчитель/учень прокрутив угору (перечитати), нові повідомлення його не відкидають вниз
  const atBottom = useRef(true);
  const onScroll = () => {
    const el = body.current;
    if (el) atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
  };
  // нове повідомлення — прокрутити вниз, як у месенджері (лише якщо стрічка й так унизу)
  useEffect(() => {
    if (body.current && atBottom.current) body.current.scrollTop = body.current.scrollHeight;
  }, [shown]);
  useEffect(() => {
    atBottom.current = true;
    setShown(0);
    let n = 0;
    const timer = setInterval(() => {
      n += 1;
      setShown(n);
      if (n >= messages.length) clearInterval(timer);
    }, 1100);
    return () => clearInterval(timer);
  }, [messages.length, run, slideId]);
  return (
    <s.Chat>
      <s.ChatHead>
        <b>{heading || "Chat"}</b>
        <button type="button" onClick={() => setRun((r) => r + 1)} title="Replay">
          ↻
        </button>
      </s.ChatHead>
      <s.ChatBody ref={body} onScroll={onScroll}>
        {messages.slice(0, shown).map((m, i) => {
          const mine = m.name === me;
          const first = i === 0 || messages[i - 1].name !== m.name;
          return (
            <s.Bubble key={`${run}-${i}`} $mine={mine}>
              {first && !mine && <small>{m.name}</small>}
              <Marked text={m.text} />
            </s.Bubble>
          );
        })}
        {shown < messages.length && (
          <s.Typing $mine={messages[shown]?.name === me}>
            <i />
            <i />
            <i />
          </s.Typing>
        )}
      </s.ChatBody>
    </s.Chat>
  );
}

// ---------- reading ----------
// data: { heading?, paragraphs: [string], lines (як у choose), images?: [{ url, label }], compact?, dense?, bigImages? }
// dense: true — дрібніші питання праворуч (коли фото всередині статті, а питань багато)
// paragraphs: рядок = абзац; { image, caption } = фото всередині статті
// bigImages: true — фото великі й цілі (contain), а не мініатюри-смужка (напр. «яке фото до розмови?»)
// Текст ліворуч (**жирне** виділяється), питання праворуч — учень бачить текст, поки відповідає
function Reading({ slide, showAnswers }) {
  // images — маленькі фото з підписами (напр. A–D), якщо питання до тексту посилаються на них
  const { heading, paragraphs = [], lines = [], compact, images = [], bigImages } = slide.data || {};
  return (
    <s.ReadingLayout>
      {slide.data?.chat ? (
        <ChatThread
          heading={heading}
          me={slide.data.chat.me}
          slideId={slide.id}
          messages={paragraphs
            .filter((text) => typeof text === "string")
            .map((text) => {
              const at = text.indexOf(":");
              return { name: text.slice(0, at).trim(), text: text.slice(at + 1).trim() };
            })}
        />
      ) : slide.data?.mock ? (
        <Mock data={slide.data} />
      ) : (
      <s.Article $compact={compact}>
        {heading && <h3>{heading}</h3>}
        {/* абзац — рядок або { image, caption } — фото всередині статті (як стаття на телефоні) */}
        {paragraphs.map((text, i) =>
          typeof text === "string" ? (
            <p key={i}>
              <Marked text={text} />
            </p>
          ) : (
            <s.ArticleFigure key={i} $tall={paragraphs.length === 1}>
              <img src={mediaUrl(text.image)} alt="" loading="lazy" />
              {text.caption && <figcaption>{text.caption}</figcaption>}
            </s.ArticleFigure>
          )
        )}
      </s.Article>
      )}
      <s.ReadingSide>
        {images.length > 0 && (
          <s.ChooseImages $count={images.length} $thumbs={!bigImages} $big={bigImages}>
            {images.map((image) => (
              <s.ChooseImage key={image.url} $contain={bigImages}>
                <img src={mediaUrl(image.url)} alt="" />
                {image.label && <figcaption>{image.label}</figcaption>}
              </s.ChooseImage>
            ))}
          </s.ChooseImages>
        )}
        <ChooseLines slideId={slide.id} lines={lines} compact={compact || lines.length > 6} dense={slide.data?.dense || lines.length > 6 || images.length > 0} showAnswers={showAnswers} />
      </s.ReadingSide>
    </s.ReadingLayout>
  );
}

// ---------- grammar ----------
// data: { tables: [{ title, rows: [[cell, …]] }], notes: [string], cols? } — **жирне** виділяється
function Grammar({ slide }) {
  const { tables = [], notes = [], cols = 2 } = slide.data || {};
  return (
    <s.GrammarLayout>
      <GrammarVisual data={slide.data} />
      <s.Tables $cols={cols}>
        {tables.map((table) => (
          <s.Table key={table.title}>
            <h3>{table.title}</h3>
            <table>
              <tbody>
                {table.rows.map((row, i) => (
                  <tr key={i}>
                    {row.map((cell, j) => (
                      <td key={j}>
                        <Marked text={cell} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </s.Table>
        ))}
      </s.Tables>
      {notes.length > 0 && (
        <s.Notes>
          {notes.map((note) => (
            <s.Note key={note}>
              <Marked text={note} />
            </s.Note>
          ))}
        </s.Notes>
      )}
    </s.GrammarLayout>
  );
}

// ---------- unscramble ----------
// data: { items: [{ words: ["from", "I'm", "India"], end: ".", answer: "I'm from India." }] }
// Клік по слову — у речення; клік у реченні — назад. Перевірка, коли використані всі слова.
// Перетягування: слово з пулу або з речення кидаємо на слово в реченні — стає на його місце (решта зсувається);
// у кінець речення — в кінець; назад у пул — прибирається з речення.
const sentenceOf = (item, built) => built.map((i) => item.words[i]).join(" ") + (item.end || "");
const DRAG = "application/x-scramble-word";

function ScrambleItem({ item, index, showAnswers, seed, built, setBuilt }) {
  const shuffled = useMemo(() => shuffleStable(item.words.map((word, i) => ({ word, i })), seed), [item.words, seed]);
  const [over, setOver] = useState(null);
  const done = built.length === item.words.length;
  const sentence = sentenceOf(item, built);
  const state = showAnswers ? "ok" : done ? (matches(sentence, item.answer, false) ? "ok" : "bad") : null;
  const dragged = (event) => {
    const raw = event.dataTransfer.getData(DRAG);
    return raw === "" ? null : Number(raw);
  };
  // слово i ставимо перед позицією pos (pos = built.length — у кінець)
  const placeAt = (i, pos) => {
    const without = built.filter((x) => x !== i);
    const from = built.indexOf(i);
    const target = from !== -1 && from < pos ? pos - 1 : pos;
    without.splice(Math.max(0, Math.min(target, without.length)), 0, i);
    setBuilt(without);
  };
  const dragProps = (i) => ({
    draggable: !showAnswers,
    onDragStart: (event) => {
      event.dataTransfer.setData(DRAG, String(i));
      event.dataTransfer.effectAllowed = "move";
    },
    onDragEnd: () => setOver(null),
  });

  return (
    <s.ScrambleRow>
      <b>{index + 1}</b>
      <s.Built
        $state={state}
        onDragOver={(event) => {
          event.preventDefault();
          if (over === null) setOver(built.length);
        }}
        onDragLeave={() => setOver(null)}
        onDrop={(event) => {
          event.preventDefault();
          const i = dragged(event);
          setOver(null);
          if (i !== null && !showAnswers) placeAt(i, built.length);
        }}
      >
        {showAnswers ? (
          item.answer.split("|")[0]
        ) : (
          <>
            {built.map((i, pos) => (
              <s.Word
                key={i}
                {...dragProps(i)}
                onClick={() => setBuilt(built.filter((x) => x !== i))}
                onDragOver={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setOver(pos);
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  const dropped = dragged(event);
                  setOver(null);
                  if (dropped !== null && dropped !== i) placeAt(dropped, pos);
                }}
                style={over === pos ? { boxShadow: "-4px 0 0 0 #f2a93b" } : undefined}
              >
                {/* перше слово речення — з великої літери */}
                {pos === 0 ? item.words[i].charAt(0).toUpperCase() + item.words[i].slice(1) : item.words[i]}
              </s.Word>
            ))}
            {done && item.end && <s.End>{item.end}</s.End>}
          </>
        )}
      </s.Built>
      <s.Pool
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => {
          event.preventDefault();
          const i = dragged(event);
          if (i !== null) setBuilt(built.filter((x) => x !== i));
        }}
      >
        {shuffled.map(({ word, i }) => (
          <s.Word key={i} {...dragProps(i)} $hidden={built.includes(i) || showAnswers} onClick={() => setBuilt([...built, i])}>
            {word}
          </s.Word>
        ))}
      </s.Pool>
    </s.ScrambleRow>
  );
}

function Unscramble({ slide, showAnswers }) {
  const { items = [] } = slide.data || {};
  const [built, setBuiltAll] = useSlideState(slide.id, "built", {});
  const done = (item, i) => (built[i] || []).length === item.words.length;
  useReport(
    slide.id,
    items.filter((item, i) => done(item, i) && matches(sentenceOf(item, built[i]), item.answer, false)).length,
    items.length,
    items.filter((item, i) => (built[i] || []).length > 0).length
  );
  return (
    <s.Scramble>
      {items.map((item, i) => (
        <ScrambleItem
          key={i}
          item={item}
          index={i}
          showAnswers={showAnswers}
          seed={`${slide.id}-${i}`}
          built={built[i] || []}
          setBuilt={(next) => setBuiltAll((prev) => ({ ...prev, [i]: next }))}
        />
      ))}
    </s.Scramble>
  );
}

// ---------- write ----------
// data: { lines: [{ speaker?, prompt, answer? }], strict? }
// answer є — перевірка після вводу (strict: регістр і . ? ! важливі); answer немає — вільне письмо, перевіряє вчитель.
// long — вільний текст (один рядок без відповіді на слайді): велике поле з прокруткою, Enter = новий рядок.
// Інакше поле росте разом з текстом (переноситься, а не тягнеться в один рядок), Enter = готово.
function WriteLine({ line, strict, showAnswers, compact, value, setValue, long }) {
  const [typing, setTyping] = useState(false);
  const ref = useRef(null);
  const shown = showAnswers && line.answer ? line.answer.split("|")[0] : value;
  useEffect(() => {
    const el = ref.current;
    if (!el || long) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight + 3}px`;
  }, [shown, long, compact]);
  const state = !line.answer ? null : showAnswers ? "ok" : value && !typing ? (matches(value, line.answer, strict) ? "ok" : "bad") : null;
  return (
    <s.WriteRow $speaker={Boolean(line.speaker)} $compact={compact} $long={long}>
      {line.speaker && <s.Speaker>{line.speaker}:</s.Speaker>}
      <s.Prompt>
        <Marked text={line.prompt} />
      </s.Prompt>
      <s.WriteInput
        ref={ref}
        rows={1}
        $long={long}
        value={shown}
        onChange={(event) => setValue(event.target.value)}
        onFocus={() => setTyping(true)}
        onBlur={() => setTyping(false)}
        onKeyDown={(event) => {
          if (event.key === "Enter" && !event.shiftKey && (line.answer || !long)) {
            event.preventDefault();
            event.currentTarget.blur();
          }
        }}
        readOnly={showAnswers && Boolean(line.answer)}
        placeholder={line.placeholder ? `e.g. ${line.placeholder}` : ""}
        spellCheck={false}
        $state={state}
      />
    </s.WriteRow>
  );
}

function Write({ slide, showAnswers }) {
  const { lines = [], strict } = slide.data || {};
  const compact = lines.length > 6;
  const [values, setValues] = useSlideState(slide.id, "values", {});
  const checked = lines.map((line, i) => ({ line, i })).filter(({ line }) => line.answer);
  useReport(
    slide.id,
    checked.filter(({ line, i }) => values[i] && matches(values[i], line.answer, strict)).length,
    checked.length,
    Object.values(values).filter((v) => String(v).trim()).length
  );
  // example: { prompt, answer } — приклад з книжки окремим виділеним рядком
  const example = slide.data?.example;
  const list = (
    <s.WriteList $compact={compact}>
      {example && (
        <s.WriteExample $compact={compact}>
          <span>
            <em>Example</em>
            <Marked text={example.prompt} />
          </span>
          <b>{example.answer}</b>
        </s.WriteExample>
      )}
      {lines.map((line, i) => (
        <WriteLine
          key={i}
          line={line}
          strict={strict}
          showAnswers={showAnswers}
          compact={compact}
          long={lines.length === 1 && !line.answer}
          value={values[i] || ""}
          setValue={(value) => setValues((prev) => ({ ...prev, [i]: value }))}
        />
      ))}
    </s.WriteList>
  );
  const boxed = slide.data?.wordBox ? (
    <div>
      <WordBox words={slide.data.wordBox} compact={compact} />
      {list}
    </div>
  ) : (
    list
  );
  // slide.image — фото, яке учень описує (вчитель бачить те саме й може виправити)
  if (!slide.image?.url) return boxed;
  return (
    <s.WriteWithImage>
      <s.ChooseImage $contain={slide.image.fit === "contain"}>
        <img src={mediaUrl(slide.image.url)} alt="" />
      </s.ChooseImage>
      {boxed}
    </s.WriteWithImage>
  );
}

export const EXTRA_BLOCKS = {
  choose: Choose,
  reading: Reading,
  video: Video,
  grammar: Grammar,
  unscramble: Unscramble,
  write: Write,
};
